import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import tw, { TwStyle } from 'twin.macro';

import { Loader } from '@components/Loader';
import { Spacer } from '@components/Spacer';
import { AddUserIcon } from '@icons/AddUserIcon';
import { RoleSelect, OptionType } from '@account_module/components/RoleSelect';
import { inviteUser } from '@account_module/services';
import useOnClickOutside from '@app/hooks/useOnClickOutside';
import { ORGANIZATION_QUERY_KEY } from '@account_module/constants/queryKeys';
import { useAccountSettingContext } from '@account_module/context/AccountSettingsContext';
import { InviteFormType } from '@account_module/types/Organizations';
import { useNotification } from '@insights_module/hooks/useNotification';

const Container = tw.div`relative`;
const Form = tw.form`p-4 rounded-md shadow-dropdown border border-solid border-gray-1 text-left w-[420px] bg-neutral-background z-50 absolute right-0 top-[calc(100% + 10px)]`;
const FlexContainer = tw.div`flex items-center justify-between`;
const FormTitle = tw.h5`text-sm text-text-and-icon font-semibold text-left tracking-[0.14px]`;
const SmallText = tw.p`text-[11px] text-left text-text-and-icon-muted`;
const InputLabel = tw.label`text-text-and-icon-muted text-xs font-semibold inline-block m-0`;
const ButtonsContainer = tw.div`flex flex-row gap-2`;
const FormToggler = tw.button`bg-neutral-background text-text-and-icon h-6 rounded-[4px] text-[11px] flex flex-row items-center justify-center border border-solid border-gray-1 px-[10px] whitespace-nowrap transition-all duration-100`;
const FormButton = tw.button`rounded-[4px] text-[11px] py-[4px] px-[10px] h-[25px] bg-accent-2 text-text-and-icon-invert`;
const CancelButton = tw(
  FormButton
)`bg-neutral-background text-text-and-icon border border-solid border-gray-1`;
const roleLabelStyle =
  tw`bg-transparent border border-solid border-gray-1 text-text-and-icon h-6 text-[11px] flex flex-row items-center justify-between px-[10px] whitespace-nowrap transition-all duration-100 mr-2 active:opacity-70` as TwStyle;

const options: OptionType[] = [
  {
    key: 'editor',
    label: 'User',
    title: 'User',
    description: 'Can see Audio Insights analysis, create views and export content',
  },
  {
    key: 'admin',
    label: 'Admin',
    title: 'Admin',
    description: 'Can do everything as user can and can manage content and team',
  },
  {
    key: 'viewer',
    label: 'Viewer',
    title: 'Viewer',
    description: 'Can see Audio Insights analysis',
  },
];

export async function inviteServiceFn(orgId: string | undefined, formData: InviteFormType | null) {
  return await inviteUser(orgId, formData);
}

export async function inviteBulkServiceFn(
  orgId: string | undefined,
  formData: InviteFormType,
  emails: string[]
) {
  return Promise.all(emails.map((email) => inviteUser(orgId, { ...formData, email })));
}

export const InviteForm = ({ organizationId }: { organizationId: string | undefined }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notify, NTFC_TYPE } = useNotification();
  const { isInviteUserFormVisible, setIsInviteUserFormVisible } = useAccountSettingContext();
  const { control, register, handleSubmit, reset } = useForm<InviteFormType>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formContainerRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(formContainerRef, () => setIsInviteUserFormVisible(false));

  const handleCancel = () => {
    setIsInviteUserFormVisible(!isInviteUserFormVisible);
    reset();
  };

  const handleSuccess = (msg: string) => {
    notify({
      type: NTFC_TYPE.INFO,
      message: msg,
    });
    reset();
    setIsInviteUserFormVisible(false);
  };

  const handleError = (msg: string) => {
    notify({
      type: NTFC_TYPE.WARNING,
      message: `Something whent wrong: ${msg}`,
    });
  };

  const handleFinally = () => {
    setIsLoading(false);
    queryClient.invalidateQueries([ORGANIZATION_QUERY_KEY.users]);
  };

  const onSubmit = async (formData: InviteFormType) => {
    if (isLoading) return;

    setIsLoading(true);

    const emails = formData.email.split(',').map((email) => email.trim());

    if (organizationId) {
      if (emails.length > 1) {
        inviteBulkServiceFn(organizationId, formData, emails)
          .then(() => handleSuccess('Invitations sent'))
          .catch((error) => handleError(error.message))
          .finally(handleFinally);

        return;
      }

      inviteServiceFn(organizationId, formData)
        .then(() => handleSuccess('Invitation sent'))
        .catch((error) => handleError(error.message))
        .finally(handleFinally);
    }
  };

  return (
    <Container ref={formContainerRef}>
      <FormToggler
        type='button'
        onClick={handleCancel}
        css={[isInviteUserFormVisible && tw`bg-gray-3`]}>
        <AddUserIcon tw='mr-[6px] stroke-text-and-icon-muted transition-all duration-100' />
        {t('account_module.settings_page.users_table_controler.invite_team_member')}
      </FormToggler>

      {isInviteUserFormVisible && (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormTitle>{t('account_module.settings_page.invite_form.title')}</FormTitle>
          <Spacer direction='vertical' size='sm' />
          <InputLabel htmlFor='email'>
            {t('account_module.settings_page.invite_form.input.label')}
          </InputLabel>
          <input
            type='text'
            placeholder={t('account_module.settings_page.invite_form.input.placeholder')}
            tw='w-full p-2 bg-transparent border border-solid border-gray-1 text-text-and-icon rounded text-xs my-1'
            id='email'
            {...register('email')}
          />
          <SmallText>{t('account_module.settings_page.invite_form.description')}</SmallText>

          <Spacer direction='vertical' size='md' />
          <FlexContainer>
            <Controller
              control={control}
              render={({ field: { onChange } }) => (
                <RoleSelect options={options} labelStyle={roleLabelStyle} onChange={onChange} />
              )}
              defaultValue={options[0].key}
              name='permission_type'
            />

            <ButtonsContainer>
              <CancelButton type='button' onClick={handleCancel}>
                {t('account_module.settings_page.invite_form.cta.cancel')}
              </CancelButton>
              <FormButton type='submit'>
                {isLoading ? (
                  <Loader theme='dark' size='xs' />
                ) : (
                  t('account_module.settings_page.invite_form.cta.invite')
                )}
              </FormButton>
            </ButtonsContainer>
          </FlexContainer>
        </Form>
      )}
    </Container>
  );
};
